import { forwardRef, type HTMLAttributes } from "react";
import { cn } from "@/utils/cn";

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?:
    | "default"
    | "secondary"
    | "primary"
    | "success"
    | "warning"
    | "error"
    | "outline";
  size?: "sm" | "md";
}

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  (
    { className, variant = "default", size = "md", children, ...props },
    ref,
  ) => {
    const baseStyles = "inline-flex items-center font-medium rounded-full";

    const variants = {
      default: "bg-gray-100 text-gray-800",
      secondary: "bg-gray-50 text-gray-700",
      primary: "bg-primary-100 text-primary-800",
      success: "bg-green-100 text-green-800",
      warning: "bg-yellow-100 text-yellow-800",
      error: "bg-red-100 text-red-800",
      outline: "border border-gray-300 text-gray-700 bg-transparent",
    };

    const sizes = {
      sm: "px-2 py-0.5 text-xs",
      md: "px-2.5 py-0.5 text-sm",
    };

    return (
      <span
        ref={ref}
        className={cn(baseStyles, variants[variant], sizes[size], className)}
        {...props}
      >
        {children}
      </span>
    );
  },
);

Badge.displayName = "Badge";

/** Status Badge with predefined variants for common vehicle/order statuses */
export interface StatusBadgeProps extends Omit<BadgeProps, "variant"> {
  status: "available" | "pending" | "sold" | "reserved" | "coming_soon";
}

const statusConfig: Record<
  StatusBadgeProps["status"],
  { variant: BadgeProps["variant"]; label: string }
> = {
  available: { variant: "success", label: "Available" },
  pending: { variant: "warning", label: "Pending" },
  sold: { variant: "error", label: "Sold" },
  reserved: { variant: "primary", label: "Reserved" },
  coming_soon: { variant: "default", label: "Coming Soon" },
};

export function StatusBadge({ status, ...props }: StatusBadgeProps) {
  const config = statusConfig[status];

  return (
    <Badge variant={config.variant} {...props}>
      {config.label}
    </Badge>
  );
}
